import {
  boolean,
  date,
  index,
  integer,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { nanoid } from "nanoid";
import { user } from ".";
import { branches } from "./branches";
import { productVariants } from "./catalog";

export const KYC_STATUSES = ["NONE", "PENDING", "VERIFIED", "REJECTED"] as const;
export type KycStatus = (typeof KYC_STATUSES)[number];

export const CUSTOMER_LOCALES = ["lo", "en"] as const;
export type CustomerLocale = (typeof CUSTOMER_LOCALES)[number];

export const customers = pgTable(
  "customers",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    phone: text("phone").notNull(),
    name: text("name"),
    gender: text("gender").$type<"MALE" | "FEMALE" | "OTHER">(),
    dateOfBirth: date("date_of_birth", { mode: "string" }),
    userId: text("user_id").references(() => user.id, { onDelete: "cascade" }),
    defaultBranchId: text("default_branch_id").references(() => branches.id, {
      onDelete: "set null",
    }),
    kycStatus: text("kyc_status")
      .$type<KycStatus>()
      .notNull()
      .default("NONE"),
    kycVerifiedAt: timestamp("kyc_verified_at", {
      withTimezone: true,
      mode: "date",
    }),
    kycDocumentRef: text("kyc_document_ref"),
    preferredLocale: text("preferred_locale")
      .$type<CustomerLocale>()
      .notNull()
      .default("lo"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("customers_phone_idx").on(t.phone),
    index("customers_default_branch_idx").on(t.defaultBranchId),
    index("customers_user_idx").on(t.userId),
    index("customers_kyc_status_idx").on(t.kycStatus),
  ],
);

export const loyaltyAccounts = pgTable("loyalty_accounts", {
  customerId: text("customer_id")
    .primaryKey()
    .references(() => customers.id, { onDelete: "cascade" }),
  pointsBalance: integer("points_balance").notNull().default(0),
  createdAt: timestamp("created_at", {
    withTimezone: true,
    mode: "date",
  }).notNull(),
  updatedAt: timestamp("updated_at", {
    withTimezone: true,
    mode: "date",
  }).notNull(),
});

export const loyaltyTransactions = pgTable(
  "loyalty_transactions",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    customerId: text("customer_id")
      .notNull()
      .references(() => customers.id, { onDelete: "cascade" }),
    type: text("type", { enum: ["EARN", "REDEEM"] }).notNull(),
    points: integer("points").notNull(),
    refOrderId: text("ref_order_id"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("loyalty_transactions_customer_idx").on(t.customerId),
    index("loyalty_transactions_created_idx").on(t.createdAt),
    uniqueIndex("loyalty_transactions_earn_order_uidx")
      .on(t.refOrderId)
      .where(sql`${t.type} = 'EARN' AND ${t.refOrderId} IS NOT NULL`),
  ],
);

export const rewards = pgTable("rewards", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => nanoid()),
  name: text("name").notNull(),
  requiredPoints: integer("required_points").notNull(),
  variantRewardId: text("variant_reward_id").references(
    () => productVariants.id,
    { onDelete: "set null" },
  ),
  /** S3 key or path — แสดงใน Customer App (fallback รูปสินค้า linked variant) */
  imageUrl: text("image_url"),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at", {
    withTimezone: true,
    mode: "date",
  }).notNull(),
  updatedAt: timestamp("updated_at", {
    withTimezone: true,
    mode: "date",
  }).notNull(),
});

export const rewardRedemptions = pgTable(
  "reward_redemptions",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    customerId: text("customer_id")
      .notNull()
      .references(() => customers.id, { onDelete: "cascade" }),
    rewardId: text("reward_id")
      .notNull()
      .references(() => rewards.id, { onDelete: "cascade" }),
    status: text("status")
      .$type<"PENDING" | "COMPLETED" | "CANCELLED">()
      .notNull(),
    refOrderId: text("ref_order_id"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("reward_redemptions_customer_idx").on(t.customerId),
    index("reward_redemptions_reward_idx").on(t.rewardId),
  ],
);
